import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Table } from '@frontend/features/common/components/Table/Table';
import { Link } from '@frontend/features/common/components/Link';
import { routes } from '@frontend/router/routes';
import { MetricLabel } from './PerformanceHelp';
import { closingDays } from './closing-days';
import { DayEndReport, PerformanceRequest } from './types';

export function DayEndMetrics({
  report,
  request,
  onRequest,
}: {
  report: DayEndReport;
  request: PerformanceRequest;
  onRequest: (request: PerformanceRequest) => void;
}) {
  const { t } = useTranslation();
  const label = (key: string, help: string) => (
    <MetricLabel label={t(`workflowPerformance.${key}`)} help={t(`workflowPerformance.help.${help}`)} />
  );
  const daily = report.dailyGroups ? closingDays(request.from, request.to, report.dailyGroups) : [];
  const filter = (advisor: string, team: number | null) =>
    onRequest({
      ...request,
      advisor,
      team: team ?? undefined,
      closingOffset: 0,
      closingDate: undefined,
      afterCaseId: 0,
      afterResponseCaseId: undefined,
      afterResponseId: undefined,
    });
  return (
    <Stack spacing={2}>
      <Typography variant="h6">{t('workflowPerformance.dayEnd.title')}</Typography>
      <Alert severity="info">{t('workflowPerformance.dayEnd.hint')}</Alert>
      {report.truncated && (
        <Alert severity="warning">{t('workflowPerformance.dayEnd.limit', { limit: report.limit })}</Alert>
      )}
      <Typography>{t('workflowPerformance.dayEnd.progress', report.progress)}</Typography>
      {(report.progress.waiting > 0 || report.progress.incomplete > 0 || report.progress.truncated) && (
        <Alert severity="warning">{t('workflowPerformance.dayEnd.incomplete')}</Alert>
      )}
      {Date.parse(request.from) < Date.parse(report.retainedSince) && (
        <Alert severity="warning">{t('workflowPerformance.dayEnd.retention')}</Alert>
      )}
      {report.totals && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Typography>{t('workflowPerformance.dayEnd.totals', {...report.totals})}</Typography>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2 }}
            >
              {(['evaluated', 'pending', 'pendingCases', 'unknown'] as const).map((key, index) => (
                <Stack key={key} spacing={1}>
                  {label(
                    `dayEnd.${key}`,
                    ['closingEvaluated', 'closingPending', 'closingUnique', 'closingUnknown'][index],
                  )}
                  <Typography variant="h4">{report.totals![key]}</Typography>
                </Stack>
              ))}
            </Box>
          </Stack>
        </Paper>
      )}
      <Typography variant="h6">{t('workflowPerformance.dayEnd.dailyTitle')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('workflowPerformance.dayEnd.dailyHint')}
      </Typography>
      {!report.truncated && !report.dailyGroups && (
        <Alert severity="info">{t('workflowPerformance.dayEnd.dailyUnavailable')}</Alert>
      )}
      {!report.truncated && report.dailyGroups && (
        <Table
          height={Math.min(420, Math.max(200, 90 + daily.length * 55))}
          columns={[
            { id: 'day', label: t('workflowPerformance.calendarDate') },
            { id: 'zones', label: t('workflowPerformance.calendarZone') },
            { id: 'evaluated', label: label('dayEnd.evaluated', 'closingEvaluated') },
            { id: 'pending', label: label('dayEnd.pending', 'closingPending') },
            { id: 'pendingCases', label: label('dayEnd.pendingCases', 'closingUnique') },
            { id: 'unknown', label: label('dayEnd.unknown', 'closingUnknown') },
          ]}
          data={{
            key: 'date',
            totalCount: daily.length,
            items: daily.map(({ date, counts }) => ({
              date,
              day: counts ? (
                <Button
                  aria-pressed={request.closingDate === date}
                  onClick={() => onRequest({ ...request, closingDate: date, closingOffset: 0 })}
                >
                  {date}
                </Button>
              ) : (
                date
              ),
              zones: counts ? counts.zones.join(', ') : t('workflowPerformance.dayEnd.noRecords'),
              evaluated: counts?.evaluated ?? '—',
              pending: counts?.pending ?? '—',
              pendingCases: counts?.pendingCases ?? '—',
              unknown: counts?.unknown ?? '—',
            })),
          }}
        />
      )}
      <Typography variant="h6">{t('workflowPerformance.dayEnd.byAdvisor')}</Typography>
      {report.groupsTruncated && <Alert severity="warning">{t('workflowPerformance.groupsTruncated')}</Alert>}
      <Table
        height={250}
        columns={[
          { id: 'advisorLabel', label: t('workflowPerformance.workloadAdvisor') },
          { id: 'teamLabel', label: t('workflowPerformance.workloadTeam') },
          { id: 'evaluated', label: label('dayEnd.evaluated', 'closingEvaluated') },
          { id: 'pending', label: label('dayEnd.pending', 'closingPending') },
          { id: 'unknown', label: label('dayEnd.unknown', 'closingUnknown') },
        ]}
        data={{
          key: 'id',
          totalCount: report.groups.length,
          items: report.groups.map((row, index) => ({
            ...row,
            id: index,
            advisorLabel: (
              <Button onClick={() => filter(row.advisor, row.team)}>{row.advisorName ?? row.advisor}</Button>
            ),
            teamLabel: row.teamName ?? row.team ?? '—',
          })),
        }}
      />
      <Stack direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap">
        <Typography variant="h6">
          {request.closingDate
            ? t('workflowPerformance.dayEnd.detailDate', { date: request.closingDate })
            : t('workflowPerformance.dayEnd.details')}
        </Typography>
        {request.closingDate && (
          <Button onClick={() => onRequest({ ...request, closingDate: undefined, closingOffset: 0 })}>
            {t('workflowPerformance.dayEnd.allDates')}
          </Button>
        )}
      </Stack>
      <Table
        height={320}
        columns={[
          { id: 'case', label: t('workflowPerformance.case') },
          { id: 'advisorLabel', label: t('workflowPerformance.workloadAdvisor') },
          { id: 'end', label: t('workflowPerformance.dayEnd.end') },
          { id: 'result', label: t('workflowPerformance.status') },
          { id: 'checked', label: t('workflowPerformance.dayEnd.checked') },
        ]}
        data={{
          key: 'id',
          totalCount: report.cases.length,
          items: report.cases.map((row) => ({
            ...row,
            id: `${row.caseId}:${row.closeAt}`,
            case: <Link to={routes.dashboard.caseView.replace(':id', String(row.caseId))}>#{row.caseId}</Link>,
            advisorLabel: row.advisorName ?? row.advisor,
            end: `${new Date(row.closeAt).toLocaleString(undefined, { timeZone: row.zone })} (${row.zone})`,
            result: t(`workflowPerformance.dayEnd.${row.uncertain ? 'unknown' : row.pending ? 'pending' : 'clear'}`),
            checked: new Date(row.checkedAt).toLocaleString(),
          })),
        }}
      />
      <Stack direction="row">
        <Button
          disabled={!request.closingOffset}
          onClick={() => onRequest({ ...request, closingOffset: Math.max(0, (request.closingOffset ?? 0) - 100) })}
        >
          {t('workflowPerformance.previous')}
        </Button>
        <Button
          disabled={report.nextOffset === null}
          onClick={() => onRequest({ ...request, closingOffset: report.nextOffset! })}
        >
          {t('workflowPerformance.next')}
        </Button>
      </Stack>
    </Stack>
  );
}
